import { useCallback, useEffect, useLayoutEffect, useRef, useState } from "react";
import type { DependencyList, Dispatch, SetStateAction } from "react";

// One data load = one request that always belongs to the CURRENT inputs:
//  - latest request wins: when `deps` change (or on unmount) the previous request is aborted, and a
//    response that still arrives from an older request is ignored even if the service ignores `signal`;
//  - `data` never belongs to older inputs: it is cleared in the same render in which `deps` change,
//    so a late or stale response can never paint another agent / tab / filter
//    (opt out with `keepPreviousData` where the old result should stay on screen while the next one loads,
//    e.g. filter tabs that are drawn from the result itself — the stale response is still ignored);
//  - `reload()` re-runs the request for the same inputs and keeps the data on screen while it runs.
// Pass `signal` on to the service call (services take an optional `signal?: AbortSignal`).
//
//   const { data, error, loading, reload } = useRequest((signal) => getAgentDetail(id, source, signal), [id, source]);

export interface UseRequestOptions {
  // false = do not call yet (e.g. waiting for auth). Stays idle: no data, not loading.
  enabled?: boolean;
  // true = keep showing the previous data (loading=true) when deps change instead of clearing it
  keepPreviousData?: boolean;
}

export interface UseRequestResult<T> {
  data: T | null;
  // The rejection of the latest request (axios error …), null while loading or after success
  error: unknown;
  loading: boolean;
  reload: () => void;
  // For optimistic updates of the loaded data (e.g. removing a bookmark card right away)
  setData: Dispatch<SetStateAction<T | null>>;
}

interface RequestState<T> {
  data: T | null;
  error: unknown;
  loading: boolean;
}

const sameInputs = (a: readonly unknown[], b: readonly unknown[]) => a.length === b.length && a.every((value, i) => Object.is(value, b[i]));

export function useRequest<T>(fetcher: (signal: AbortSignal) => Promise<T>, deps: DependencyList, options: UseRequestOptions = {}): UseRequestResult<T> {
  const { enabled = true, keepPreviousData = false } = options;
  const inputs = [...deps, enabled];

  const [state, setState] = useState<RequestState<T>>({ data: null, error: null, loading: enabled });
  const [reloadCount, setReloadCount] = useState(0);
  const [prevInputs, setPrevInputs] = useState(inputs);
  const keepDataRef = useRef(false);

  // The effect below always calls the fetcher of the latest committed render
  const fetcherRef = useRef(fetcher);
  useLayoutEffect(() => {
    fetcherRef.current = fetcher;
  });

  // New inputs: reset during render (React re-renders before painting), not in an effect one frame later
  if (!sameInputs(prevInputs, inputs)) {
    setPrevInputs(inputs);
    setState((prev) => ({ data: keepPreviousData ? prev.data : null, error: null, loading: enabled }));
  }

  useEffect(() => {
    if (!enabled) return;
    const controller = new AbortController();
    if (keepDataRef.current) {
      keepDataRef.current = false;
      setState((prev) => ({ data: prev.data, error: null, loading: true }));
    }
    let request: Promise<T>;
    try {
      request = fetcherRef.current(controller.signal);
    } catch (error) {
      request = Promise.reject(error);
    }
    request.then(
      (data) => {
        if (!controller.signal.aborted) setState({ data, error: null, loading: false });
      },
      (error: unknown) => {
        if (!controller.signal.aborted) setState((prev) => ({ data: prev.data, error, loading: false }));
      },
    );
    return () => controller.abort();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [...deps, enabled, reloadCount]);

  const reload = useCallback(() => {
    keepDataRef.current = true;
    setReloadCount((count) => count + 1);
  }, []);

  const setData = useCallback<Dispatch<SetStateAction<T | null>>>((next) => {
    setState((prev) => ({ ...prev, data: typeof next === "function" ? (next as (value: T | null) => T | null)(prev.data) : next }));
  }, []);

  return { data: state.data, error: state.error, loading: state.loading, reload, setData };
}
